import { gql, TypedDocumentNode } from "@apollo/client";

export type CareerConsultantSection = {
  id: string;
  name: string;
  legacyId: string | null;
};

export type MeCareerConsultantSectionsResponse = {
  me: { id: string } | null;
  careerConsultantSections: CareerConsultantSection[];
};

export const ME_CAREER_CONSULTANT_SECTIONS_QUERY: TypedDocumentNode<MeCareerConsultantSectionsResponse> = gql`
  query MeCareerConsultantSections {
    me {
      id
    }
    careerConsultantSections {
      id
      name
      legacyId
    }
  }
`;

export const QUICK_ADD_MENTION_HITS_PER_PAGE = 15;

export const QUICK_ADD_WORKPLACE_PEOPLE_STATUSES = ["ACTIVE", "SUSPENDED", "TERMINATED"] as const;

export type QuickAddPersonProgramNode = {
  status?: string | null;
  band?: {
    createdReason?: string | null;
    classification?: { name?: string | null; description?: string | null } | null;
  } | null;
};

export type QuickAddPersonSearchNode = {
  id: string;
  pgaId: string | null;
  displayName: string;
  email?: string | null;
  profilePhoto?: string | null;
  primarySection?: { id: string | null; name: string | null } | null;
  primaryFacility?: {
    id: string | null;
    name: string | null;
    addresses?: { city?: string | null; state?: string | null }[] | null;
  } | null;
  employments?: { facility?: { id?: string | null } | null }[] | null;
  latestProgram?: QuickAddPersonProgramNode | null;
};

export type QuickAddFacilitySearchNode = {
  id: string;
  name: string;
  legacyId: string | null;
  section?: { id: string; name: string | null } | null;
  mainAddress?: { city: string | null; state: string | null } | null;
};

export type QuickAddFuzzyPeopleSearchResponse = {
  fuzzySearchPeople: { nodes: QuickAddPersonSearchNode[] } | null;
};

export type QuickAddFuzzyPeopleSearchVariables = {
  search: string;
  first: number;
};

export type QuickAddFuzzyFacilitiesSearchResponse = {
  fuzzySearchFacilities: { nodes: QuickAddFacilitySearchNode[] } | null;
};

export type QuickAddFuzzyFacilitiesSearchVariables = {
  search: string;
  first: number;
};

export type QuickAddWorkplacePeopleResponse = {
  people: { nodes: QuickAddPersonSearchNode[] } | null;
};

export type QuickAddWorkplacePeopleVariables = {
  input: {
    facilityIds: string[];
    statuses: Array<(typeof QUICK_ADD_WORKPLACE_PEOPLE_STATUSES)[number]>;
  };
  first: number;
};

const PERSON_SELECTION = `
        id
        pgaId
        displayName
        profilePhoto
        primarySection {
          id
          name
        }
        primaryFacility {
          id
          name
          addresses {
            city
            state
          }
        }
        employments {
          facility {
            id
          }
        }
`;

const PERSON_MEMBERSHIP_SELECTION = `
        id
        pgaId
        latestProgram {
          status
          band {
            createdReason
            classification {
              name
              description
            }
          }
        }
`;

export const QUICK_ADD_FUZZY_PEOPLE_SEARCH_QUERY: TypedDocumentNode<
  QuickAddFuzzyPeopleSearchResponse,
  QuickAddFuzzyPeopleSearchVariables
> = gql`
  query QuickAddFuzzyPeopleSearch($search: String!, $first: Int!) {
    fuzzySearchPeople(input: { search: $search }, first: $first) {
      nodes {
        ${PERSON_SELECTION}
      }
    }
  }
`;

export const QUICK_ADD_FUZZY_FACILITIES_SEARCH_QUERY: TypedDocumentNode<
  QuickAddFuzzyFacilitiesSearchResponse,
  QuickAddFuzzyFacilitiesSearchVariables
> = gql`
  query QuickAddFuzzyFacilitiesSearch($search: String!, $first: Int!) {
    fuzzySearchFacilities(input: { search: $search, excludeArchived: true }, first: $first) {
      nodes {
        id
        name
        legacyId
        section {
          id
          name
        }
        mainAddress {
          city
          state
        }
      }
    }
  }
`;

export const QUICK_ADD_WORKPLACE_PEOPLE_QUERY: TypedDocumentNode<
  QuickAddWorkplacePeopleResponse,
  QuickAddWorkplacePeopleVariables
> = gql`
  query QuickAddWorkplacePeople($input: BackofficeCRMPeopleInput!, $first: Int!) {
    people(first: $first, input: $input) {
      nodes {
        ${PERSON_SELECTION}
      }
    }
  }
`;

export type QuickAddPeopleMembershipResponse = {
  people: { nodes: QuickAddPersonSearchNode[] } | null;
};

export type QuickAddPeopleMembershipVariables = {
  input: { pgaIds: string[] };
  first: number;
};

export const QUICK_ADD_PEOPLE_MEMBERSHIP_QUERY: TypedDocumentNode<
  QuickAddPeopleMembershipResponse,
  QuickAddPeopleMembershipVariables
> = gql`
  query QuickAddPeopleMembership($input: BackofficeCRMPeopleInput!, $first: Int!) {
    people(first: $first, input: $input) {
      nodes {
        ${PERSON_MEMBERSHIP_SELECTION}
      }
    }
  }
`;
